import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallback } from "react";
import { isModulePermanentlyHidden } from "@/config/hiddenModules";

export interface ModuleSetting {
  id: string;
  module_key: string;
  label: string;
  enabled: boolean;
  description: string;
  sort_order: number;
  updated_at: string;
  min_verification_tier: number;
}

const CORE_MODULES = ["overview", "account", "kyc_verification"];

export function useModuleSettings() {
  const queryClient = useQueryClient();

  const { data: modules = [], isLoading, isFetched } = useQuery<ModuleSetting[]>({
    queryKey: ["module-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_settings" as any)
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return (data as any[]) ?? [];
    },
    // Refreshed by realtime (useConfigRealtime) and on session change.
    staleTime: 30 * 1000,
    refetchOnMount: true,
  });

  const isModuleEnabled = useCallback(
    (key: string) => {
      if (isModulePermanentlyHidden(key)) return false;
      if (CORE_MODULES.includes(key)) return true;
      // Until settings have actually resolved, hide non-core modules so a
      // disabled module can never flash on screen.
      if (isLoading || !isFetched || modules.length === 0) return false;
      const mod = modules.find((m) => m.module_key === key);
      return mod ? mod.enabled : true;
    },
    [modules, isLoading, isFetched]
  );


  const getModuleMinTier = useCallback(
    (key: string): number => {
      const mod = modules.find((m) => m.module_key === key);
      return mod?.min_verification_tier ?? 0;
    },
    [modules]
  );

  const isCoreModule = (key: string) => CORE_MODULES.includes(key);

  const toggleModule = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("module_settings" as any)
        .update({ enabled, updated_at: new Date().toISOString() } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["module-settings"] });
      const previous = queryClient.getQueryData<ModuleSetting[]>(["module-settings"]);
      queryClient.setQueryData<ModuleSetting[]>(["module-settings"], (old) =>
        (old ?? []).map((m) => (m.id === id ? { ...m, enabled } : m))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["module-settings"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["module-settings"] });
    },
  });

  const updateMinTier = useMutation({
    mutationFn: async ({ id, min_verification_tier }: { id: string; min_verification_tier: number }) => {
      const { error } = await supabase
        .from("module_settings" as any)
        .update({ min_verification_tier, updated_at: new Date().toISOString() } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async ({ id, min_verification_tier }) => {
      await queryClient.cancelQueries({ queryKey: ["module-settings"] });
      const previous = queryClient.getQueryData<ModuleSetting[]>(["module-settings"]);
      queryClient.setQueryData<ModuleSetting[]>(["module-settings"], (old) =>
        (old ?? []).map((m) => (m.id === id ? { ...m, min_verification_tier } : m))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["module-settings"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["module-settings"] });
    },
  });

  return { modules, isLoading, isModuleEnabled, getModuleMinTier, isCoreModule, toggleModule, updateMinTier };
}
